// External Imports
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';

import * as formik from 'formik';
import * as yup from 'yup';

//Components
import ProcessingAlert from './ProcessingAlert';
import ListVouchers from './ListVouchers';

import { fetchCustomerVouchers, queueVoucher } from '../utils/quota';

export default function ListAllocatedVouchers({ customer, bearerToken }) {
  // Local Data
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState({ message: '', severity: 'primary' });

  const schema = yup.object().shape({
    activation_code: yup.string().required(),
  });
  const { Formik } = formik;

  const getAllocatedVouchers = async () => {
    setError({ message: '', severity: 'success' });
    setLoading(true);

    await fetchCustomerVouchers(bearerToken, customer.customer_id)
      .then((data) => {
        setVouchers(data);
      })
      .catch((error) => {
        setVouchers([]);
        setError({
          message: 'Could not retrieve vouchers for customer! ' + error,
          severity: 'danger',
        });
      });

    setLoading(false);
  };

  // Event Handlers
  const handleForm = async (values, { resetForm }) => {
    const voucher = {
      activation_code: values.activation_code,
    };

    await queueVoucher(bearerToken, customer.customer_id, voucher)
      .then((data) => {
        setError({ message: 'Voucher allocated to customer!', severity: 'success' });
        setProcessing(true);
      })
      .catch((error) => {
        setError({
          message: 'Could not allocate voucher to customer! ' + error,
          severity: 'danger',
        });
      });
  };

  useEffect(() => {
    getAllocatedVouchers();
  }, [customer]);

  // Event Handlers
  const updateVoucherList = (tf) => {
    getAllocatedVouchers();
    setProcessing(tf);
  };

  return (
    <>
      {error.message != '' && (
        <Alert variant={error.severity}>
          <span>{error.message}</span>
        </Alert>
      )}

      {processing && <ProcessingAlert changeFunction={updateVoucherList} />}

      {loading ? (
        <Alert variant='warning'>
          <Spinner animation='border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </Spinner>
          <span>Vouchers loading...</span>
        </Alert>
      ) : (
        <ListVouchers
          title='Vouchers allocated to customer'
          customer_id={customer.customer_id}
          vouchers={vouchers}
          updateVoucherList={updateVoucherList}
          bearerToken={bearerToken}
        />
      )}

      <hr />

      <Formik
        validationSchema={schema}
        onSubmit={async (values, { resetForm }) => {
          await handleForm(values, { resetForm });
        }}
        initialValues={{
          activation_code: '',
        }}
      >
        {({ handleSubmit, handleChange, isSubmitting, values, errors }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Form.Group as={Row} className='mb-3' controlId='activation_code'>
              <Form.Label column sm={2}>
                Activation Code
              </Form.Label>
              <Col sm={3}>
                <Form.Control
                  type='text'
                  placeholder='Activation Code'
                  name='activation_code'
                  value={values.activation_code}
                  onChange={handleChange}
                  isInvalid={!!errors.activation_code}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.activation_code}
                </Form.Control.Feedback>
              </Col>
              <Col sm={3}>
                {isSubmitting ? (
                  <Alert variant='primary'>
                    <span>Calling API...</span>
                  </Alert>
                ) : (
                  <Button variant='warning' type='submit'>
                    Add voucher to account
                  </Button>
                )}
              </Col>
            </Form.Group>
            <Form.Text muted>
              Add vouchers for easier lookup and extra security.
              <br />
              Allocated vouchers can only be used by the customer they are allocated to.
              <br />
              Release a voucher to use on another account.
            </Form.Text>
          </Form>
        )}
      </Formik>
    </>
  );
}

ListAllocatedVouchers.propTypes = {
  customer: PropTypes.object,
  bearerToken: PropTypes.string,
};
